import { RichText } from '../i18n/RichText';
import { isolate } from '../i18n/translate';
import { t } from '../services/language-manager';
import logo from '../assets/mediabriz-logo-svg-400px.svg';
import chromeIcon from '../assets/icon-chrome.svg';
import edgeIcon from '../assets/icon-edge.svg';

interface SetupCompletePageProps {
  orgShortName: string;
  dataRegion: string;
  gcEnvironment: string;
  /** Same flag Landing uses for Settings: the user can manage settings. */
  showSettings?: boolean;
  /** `aiActionsEnabled !== false` — same flag Landing uses for Conversation Actions. */
  showActions?: boolean;
  /**
   * Operator flag; false for a Login Limit only org (pre-provisioned, update mode):
   * no extension or conversation steps, a Login Limit step instead.
   */
  conversationsEnabled?: boolean;
  onContinue: () => void;
}

const LOGIN_LIMIT_IT_GUIDE_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000476713-how-do-i-give-users-the-mediabriz-login-page-';

function continueHelpKey(showSettings: boolean, showActions: boolean): string {
  if (showActions && showSettings) return 'complete-continue-help-insights-actions-settings';
  if (showActions) return 'complete-continue-help-insights-actions';
  if (showSettings) return 'complete-continue-help-insights-settings';
  return 'complete-continue-help-insights';
}

export function SetupCompletePage({
  orgShortName,
  dataRegion,
  gcEnvironment,
  showSettings = false,
  showActions = false,
  conversationsEnabled = true,
  onContinue,
}: SetupCompletePageProps) {
  // EU-region customers must allow both the universal Mediabriz domains and
  // the EU-specific ones; US-region customers only need the universal set.
  const isEU = dataRegion === 'EU';

  // Streaming domain is region-specific; resolved from the customer's actual
  // Genesys Cloud environment (e.g. mypurecloud.com, mypurecloud.de, euw2.pure.cloud).
  const streamingDomain = `streaming.${gcEnvironment}`;

  return (
    <div className="card complete-page">
      <img src={logo} alt="Mediabriz" style={{ height: '48px', marginBottom: '12px' }} />
      <div className="complete-page__title">{t('complete-title')}</div>
      <div className="complete-page__message">{t('complete-success')}</div>

      <div className="next-steps">
        <div className="next-steps__title">{t('complete-next-steps')}</div>
        <ol className="next-steps__list">
          {conversationsEnabled && (
            <>
              <li className="next-steps__item">
                <strong>{t('complete-step-1-title')}</strong>
                <p>{t('complete-step-1')}</p>
                <div className="store-links">
                  <a
                    href="https://chromewebstore.google.com/detail/mediabriz/dojbgeididmgnlieckgekbfdncaamhbm"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-card"
                  >
                    <img className="store-card__icon" src={chromeIcon} alt="Chrome" width="32" height="32" />
                    <div className="store-card__text">
                      <span className="store-card__name">Chrome</span>
                      <span className="store-card__sub">Google Workspace</span>
                    </div>
                  </a>
                  <a
                    href="https://microsoftedge.microsoft.com/addons/detail/mediabriz/bpdjeihgofhpojeghkomckhgahioiomo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-card"
                  >
                    <img className="store-card__icon" src={edgeIcon} alt="Edge" width="32" height="32" />
                    <div className="store-card__text">
                      <span className="store-card__name">Edge</span>
                      <span className="store-card__sub">Microsoft Intune</span>
                    </div>
                  </a>
                </div>
              </li>
              <li className="next-steps__item">
                <strong>{t('complete-step-2-title')}</strong>
                {orgShortName ? (
                  <p>
                    <RichText
                      k="complete-step-2-with-name"
                      values={{
                        section: <strong>{t('complete-step-2-section')}</strong>,
                        org: <strong>{isolate(orgShortName)}</strong>,
                      }}
                    />
                  </p>
                ) : (
                  <p>{t('complete-step-2-intro-manual')}</p>
                )}
                <p className="complete-step__alt">
                  <RichText
                    k="complete-step-2-alt"
                    values={{
                      guide: (
                        <a
                          href="https://mediabriz.com/docs/enterprise-deployment"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('complete-step-2-alt-link')}
                        </a>
                      ),
                    }}
                  />
                </p>
              </li>
            </>
          )}
          <li className="next-steps__item">
            <strong>{t('complete-step-3-title')}</strong>
            {conversationsEnabled && (
              <>
                <p className="complete-step__perm">
                  <RichText
                    k="complete-step-3-conversations"
                    values={{
                      analytics: <code className="permission-code" dir="ltr">analytics:conversationDetail:view</code>,
                      and: <strong>{t('complete-step-3-and')}</strong>,
                      messages: <code className="permission-code" dir="ltr">conversation:message:view</code>,
                    }}
                  />
                </p>
                <p className="complete-step__perm">
                  <RichText
                    k="complete-step-3-contacts"
                    values={{
                      contacts: <code className="permission-code" dir="ltr">externalContacts:contact:view</code>,
                    }}
                  />
                </p>
                <p className="complete-step__perm">
                  <RichText
                    k="complete-step-3-transcription"
                    values={{
                      transcription: <code className="permission-code" dir="ltr">conversation:transcription:view</code>,
                    }}
                  />
                </p>
              </>
            )}
            <p className="complete-step__perm">
              <RichText
                k={conversationsEnabled ? 'complete-step-3-open' : 'complete-step-3-open-login-limit'}
                values={{
                  path: <strong dir="ltr">Apps → MediaBriz</strong>,
                }}
              />
            </p>
            <p className="complete-step__perm">
              <RichText
                k="complete-step-3-insights"
                values={{
                  view: <code className="permission-code" dir="ltr">integration:cloudBriz:view</code>,
                  admin: <code className="permission-code" dir="ltr">integration:cloudBriz:admin</code>,
                }}
              />
            </p>
            <p className="complete-step__perm">
              <RichText
                k={conversationsEnabled ? 'complete-step-3-admin' : 'complete-step-3-admin-login-limit'}
                values={{
                  admin: <code className="permission-code" dir="ltr">integration:cloudBriz:admin</code>,
                }}
              />
            </p>
            {conversationsEnabled && (
              <p className="complete-step__ref">
                <RichText
                  k="complete-step-3-docs"
                  values={{
                    doc: (
                      <a href="https://support.mediabriz.com/support/solutions/articles/158000451027-extention-install-guide" target="_blank" rel="noopener noreferrer">
                        {t('complete-ref-link')}
                      </a>
                    ),
                  }}
                />
              </p>
            )}
          </li>
          {!conversationsEnabled && (
            <li className="next-steps__item">
              <strong>{t('complete-login-limit-title')}</strong>
              <p>{t('complete-login-limit-setup')}</p>
              <p className="complete-step__ref">
                <RichText
                  k="complete-step-3-docs"
                  values={{
                    doc: (
                      <a href={LOGIN_LIMIT_IT_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                        {t('complete-login-limit-guide-link')}
                      </a>
                    ),
                  }}
                />
              </p>
            </li>
          )}
          <li className="next-steps__item">
            <strong>{t('complete-step-4-title')}</strong>
            <p>{t('complete-step-4-intro')}</p>
            <div className="firewall-domains">
              <table className="firewall-domains__table">
                <thead>
                  <tr>
                    <th>{t('complete-step-4-col-domain')}</th>
                    <th>{t('complete-step-4-col-purpose')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!conversationsEnabled && (
                    <tr>
                      <td><code>login.mediabriz.com</code></td>
                      <td>{t('complete-step-4-login-page')}</td>
                    </tr>
                  )}
                  <tr>
                    <td><code>api.mediabriz.com</code></td>
                    <td>{t(isEU ? 'complete-step-4-shared-api' : 'complete-step-4-us-api')}</td>
                  </tr>
                  {conversationsEnabled && (
                    <tr>
                      <td><code>media.mediabriz.com</code></td>
                      <td>{t(isEU ? 'complete-step-4-shared-media' : 'complete-step-4-us-media')}</td>
                    </tr>
                  )}
                  {isEU && (
                    <>
                      <tr>
                        <td><code>eu.api.mediabriz.com</code></td>
                        <td>{t('complete-step-4-eu-api')}</td>
                      </tr>
                      {conversationsEnabled && (
                        <tr>
                          <td><code>eu.media.mediabriz.com</code></td>
                          <td>{t('complete-step-4-eu-media')}</td>
                        </tr>
                      )}
                    </>
                  )}
                  <tr>
                    <td><code>insights.mediabriz.com</code></td>
                    <td>{t('complete-step-4-insights')}</td>
                  </tr>
                  {conversationsEnabled && (
                    <tr>
                      <td><code>view.officeapps.live.com</code></td>
                      <td>{t('complete-step-4-office')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {conversationsEnabled && (
              <p className="complete-step__ref">
                <RichText
                  k="complete-step-4-websocket"
                  values={{ domain: <code dir="ltr">{streamingDomain}</code> }}
                />
              </p>
            )}
            <p className="complete-step__ref">
              <RichText
                k="complete-step-4-docs"
                values={{
                  doc: (
                    <a href="https://mediabriz.com/security#corporate-firewall-requirements" target="_blank" rel="noopener noreferrer">
                      {t('complete-step-4-ref-link')}
                    </a>
                  ),
                }}
              />
            </p>
          </li>
        </ol>
      </div>

      <div className="complete-page__continue">
        <div className="btn-group">
          <button type="button" className="btn btn--primary" onClick={onContinue}>
            {t('complete-continue-cta')}
          </button>
        </div>
        <p className="complete-page__continue-help">
          {t(continueHelpKey(showSettings, showActions))}
        </p>
      </div>

      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '20px' }}>
        <RichText
          k="complete-footer"
          values={{
            site: (
              <a href="https://mediabriz.com" target="_blank" rel="noopener noreferrer">
                {t('complete-footer-link')}
              </a>
            ),
          }}
        />
      </p>
    </div>
  );
}
